/**
 * WL-K14 — „Geschlossener Kreis" (Plausibilität): das Hinweis-Panel.
 *
 * Zeigt die eigenen Doppelzahlungs-/Doppelbeleg-/OP-Plausibilitäts-/Skonto-
 * Frist-Hinweise (`getClosedLoopFindings`) als kompakte Liste. Rein informativ —
 * KEIN Fehler/Betrugs-Wording (sachlich: „bitte prüfen"), keine Buchungs-
 * entscheidung.
 *
 * OUTER/INNER-SPLIT (Pflicht-Muster für flag-gated Hooks, siehe
 * `~/claude-memory/knowledge-base` „flag-gated-hook-needs-component-split"):
 * {@link ClosedLoopHints} (outer) liest NUR `useAuth()` und entscheidet rein
 * synchron (`isLoopBBeta`), OB überhaupt gerendert wird. Der eigentliche
 * Daten-Fetch (`useEffect` + `getClosedLoopFindings()`) lebt ausschließlich in
 * {@link ClosedLoopHintsInner} — für einen Nicht-Beta-Nutzer wird dieser Hook
 * NIE aufgerufen (kein unnötiger `permission-denied`-Callable-Roundtrip, kein
 * bedingter Hook-Aufruf in einer einzigen Komponente).
 *
 * @module components/moat/ClosedLoopHints
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { canUseDocChecks } from '../../config/kanzleiAccess';
import { getClosedLoopFindings, ClosedLoopFinding } from '../../api/closedLoopApi';
import { centsToEuros } from '../../utils/moneyUtils';
import logger from '../../utils/logger';

/** Formats a euro-cent value to a locale currency string. */
function formatEuros(cents: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(
    centsToEuros(cents)
  );
}

/** Formats an ISO date (`YYYY-MM-DD`) to a locale date string. */
function formatDate(iso: string, locale: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Renders one finding as a compact hint line. Unknown `checkId`/`code`
 * combinations are skipped (fail-closed on the DISPLAY side too — no generic
 * "Hinweis" without a matched, deliberately worded template).
 */
function HintLine({
  finding,
  locale,
  t,
}: {
  finding: ClosedLoopFinding;
  locale: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}): React.ReactElement | null {
  const ev = finding.evidence;
  switch (finding.code) {
    case 'possible_double_payment':
      return (
        <li className="text-sm text-[var(--tone-warning-fg)]">
          {t('closedLoop.doublePayment', {
            overpaid: formatEuros(Number(ev.overpaidCents) || 0, locale),
          })}
        </li>
      );
    case 'possible_duplicate_expense':
      return (
        <li className="text-sm text-[var(--tone-warning-fg)]">
          {t('closedLoop.duplicateExpense', {
            supplier: String(ev.supplierName ?? ''),
            invoiceNumber: String(ev.invoiceNumber ?? ''),
          })}
        </li>
      );
    case 'payment_on_cancelled_invoice':
      return (
        <li className="text-sm text-[var(--tone-warning-fg)]">{t('closedLoop.paymentOnCancelled')}</li>
      );
    case 'skonto_window_open':
      return (
        <li className="text-sm text-[var(--tone-success-fg)]">
          {t('closedLoop.skontoWindow', {
            date: formatDate(String(ev.skontoDeadlineISO ?? ''), locale),
            percentage: String(ev.skontoPercentage ?? ''),
          })}
        </li>
      );
    default:
      return null;
  }
}

/** Inner component: owns the data fetch. Only ever mounted for a beta user. */
function ClosedLoopHintsInner(): React.ReactElement | null {
  const { t, i18n } = useTranslation('openItems');
  const [findings, setFindings] = useState<ClosedLoopFinding[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getClosedLoopFindings()
      .then((result) => {
        if (!cancelled) setFindings(result.findings);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Non-fatal: a plausibility panel failing to load must never break the
        // surrounding page (OP dashboard). Log + hide the panel.
        logger.warn('ClosedLoopHints: failed to load findings', err);
        setError('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-GB';

  if (error) return null;
  if (findings === null) return null; // loading — no spinner, non-blocking widget
  if (findings.length === 0) return null; // „alles plausibel" — kein leeres Panel anzeigen

  const lines = findings
    .map((f, idx) => <HintLine key={`${f.checkId}-${idx}`} finding={f} locale={locale} t={t} />)
    .filter((el): el is React.ReactElement => el !== null);

  if (lines.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-hairline bg-[rgb(var(--surface-raised-rgb)/0.6)] px-4 py-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
        {t('closedLoop.panelTitle')}
      </p>
      <ul className="space-y-1">{lines}</ul>
    </div>
  );
}

/**
 * Outer gate: renders {@link ClosedLoopHintsInner} ONLY for a Loop-B-Beta user.
 * Cosmetic client gate — the hard, fail-closed enforcement lives server-side in
 * `getClosedLoopFindings` (`isLoopBBetaServer`).
 */
export default function ClosedLoopHints(): React.ReactElement | null {
  const { currentUser, sellerData } = useAuth();
  if (!canUseDocChecks(currentUser?.uid, sellerData)) return null;
  return <ClosedLoopHintsInner />;
}
